import { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuthContext } from '@/contexts/AuthContext';
import { useAccountPlan } from '@/hooks/useAccountUsage';
import { useProfile } from '@/hooks/useProfile';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useCollaborations } from '@/hooks/useCollaborations';
import { generatedUiApi } from '@/services/api';
import { flattenVersions } from '@/lib/generatedUi';
import HowItWorksStrip from '@/components/shared/HowItWorksStrip';
import WhatIsAnAiServiceExplainer, {
  WHATS_AN_AI_SERVICE_TITLE,
} from '@/components/shared/WhatIsAnAiServiceExplainer';
import { LIVE_EXAMPLES } from '@/config';
import { trackEvent, currentLocationTag, toEventSlug } from '@/lib/analytics';

/**
 * Dashboard first-run onboarding state (task 525, Mockup E:
 * `project/artifacts/innovate-dashboard-onboarding-mockup.html`).
 *
 * The Dashboard has two states behind one nav item. Until the account has a
 * live AI Service, this checklist replaces the stats layout: six steps derived
 * from data the frontend already holds or can cheaply fetch — JWT claims
 * (email verified, plan slug), the endpoints list, run history, and the saved
 * generated-UI list. Nothing is persisted server-side except the User's own
 * dismissal, which rides the shared UI-preference ledger (task 526).
 *
 * "Choose your plan" is the one guided blocking step: new accounts start with
 * no plan (kv decision, 2026-08-17), and every AI action fails closed
 * server-side with 402 `no_active_plan` until one is chosen. Its action reuses
 * the ChoosePlanPrompt destination — the Plan & Billing catalog, never Stripe
 * checkout.
 */

/**
 * The hosted-UI live example wired to the final step. Task 540 consolidated
 * every demo link onto the shared `LIVE_EXAMPLES` config; the checklist row is
 * a compact inline action rather than the `SeeItLive` card, so it consumes the
 * config directly.
 */
export const HOSTED_UI_EXAMPLE_URL = LIVE_EXAMPLES.hostedUi.url;

/** Preference key for the per-User dismissal of the checklist. */
export const DASHBOARD_ONBOARDING_PANEL_KEY = 'dashboard-onboarding';

/** The plan catalog — same destination ChoosePlanPrompt's CTA uses. */
const CHOOSE_PLAN_PATH = '/settings/subscription';

export interface OnboardingStep {
  key: string;
  title: string;
  /** Absent on the two steps that are complete by the time the User is here. */
  description?: string;
  done: boolean;
}

export interface OnboardingSignals {
  /** JWT `email_verified` claim. */
  emailVerified: boolean;
  /** JWT `plan` claim is a non-empty slug. */
  hasPlan: boolean;
  /** GET /endpoints → pagination.total. */
  servicesTotal: number;
  /** GET /collaborations → pagination.total. */
  runsTotal: number;
  /** GET /generated-endpoint-uis → at least one saved version. */
  hasInterface: boolean;
}

/**
 * The six checklist steps and their completion conditions. Pure so the
 * derivation can be tested without rendering.
 *
 * `includePlanStep` carries the `innovate.fe.plan.no-plan-notices` flag
 * (task 994): with the reminders switched off the "Choose your plan" step is
 * not built at all, so the remaining five renumber and the progress count
 * follows the list. `hasPlan` is still read either way — the signal is part of
 * the checklist's data, not of the reminder.
 */
export function deriveOnboardingSteps(
  signals: OnboardingSignals,
  includePlanStep = true
): OnboardingStep[] {
  const planStep: OnboardingStep[] = includePlanStep
    ? [
        {
          key: 'plan',
          title: 'Choose your plan',
          description:
            "Pick the plan that fits — you can change it anytime. A paid plan unlocks external access.",
          done: signals.hasPlan,
        },
      ]
    : [];

  return [
    {
      key: 'account',
      title: 'Create your account',
      // Reaching an authenticated Dashboard is the proof.
      done: true,
    },
    {
      key: 'email',
      title: 'Confirm your email',
      done: signals.emailVerified,
    },
    ...planStep,
    {
      key: 'service',
      title: 'Create your first AI Service',
      description:
        'Describe what you want in plain words — a chat guide designs and builds it with you, right inside AI Services.',
      done: signals.servicesTotal > 0,
    },
    {
      key: 'run',
      title: 'Try it with real input',
      description: "Run it from the service's Testing tab, or straight from the chat that built it.",
      done: signals.runsTotal > 0,
    },
    {
      key: 'interface',
      title: 'Give it an interface',
      description: 'Ask the chat for a simple page your team can use — no code needed.',
      done: signals.hasInterface,
    },
  ];
}

/**
 * The greeting name.
 *
 * The platform `displayName` is the answer whenever it has arrived (task 596):
 * AMS resolves it from an explicitly chosen name, else first + last, else the
 * email local part, so this component no longer does that guessing itself. The
 * email/username arguments remain as the pre-load fallback — the checklist
 * renders before the profile query settles, and a nameless welcome for a beat
 * would be a visible flicker.
 */
export function onboardingGreetingName(
  displayName: string | undefined,
  email: string | undefined,
  username: string | undefined
): string {
  const name = displayName?.trim();
  if (name) return name;

  const localPart = email?.split('@')[0]?.trim();
  return localPart || username?.trim() || '';
}

/** Index of the step the User should do next, or -1 when all six are done. */
function nextStepIndex(steps: OnboardingStep[]): number {
  return steps.findIndex((step) => !step.done);
}

/** The three starter ideas from Mockup E; each prefills the compose entry. */
const EXAMPLES: Array<{ icon: string; title: string; prompt: string }> = [
  {
    icon: '🎫',
    title: 'Ticket triage',
    prompt:
      'Read incoming support tickets and tag each with urgency and topic, ready for routing.',
  },
  {
    icon: '📝',
    title: 'Meeting notes → action items',
    prompt:
      'Paste raw meeting notes and get a clean, owner-assigned action item list back.',
  },
  {
    icon: '⭐',
    title: 'Review reply drafter',
    prompt:
      "Draft on-brand replies to customer reviews, matching tone to the review's sentiment.",
  },
];

/** Ghost tiles previewing the stats state the Dashboard becomes. */
const GHOST_TILES = [
  'Deployed AI Services',
  'Calls this period',
  'Usage cost this period',
];

function StepTick({ state, number }: { state: 'done' | 'next' | 'todo'; number: number }) {
  const base =
    'h-6 w-6 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold mt-0.5';
  if (state === 'done') {
    return (
      <div className={`${base} bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200`}>
        ✓
      </div>
    );
  }
  if (state === 'next') {
    return <div className={`${base} bg-indigo-600 text-white`}>{number}</div>;
  }
  return (
    <div className={`${base} bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500`}>
      {number}
    </div>
  );
}

interface DashboardOnboardingProps {
  /** Total AI Services defined on the account (GET /endpoints pagination.total). */
  servicesTotal: number;
  /** Persist the User's dismissal of the checklist. */
  onDismiss: () => void;
}

export default function DashboardOnboarding({
  servicesTotal,
  onDismiss,
}: DashboardOnboardingProps) {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  // Plan comes from GET /api/accounts/usage (task 676), not the JWT.
  const { plan } = useAccountPlan();
  // Task 994: the "Choose your plan" step is one of the ambient pick-a-plan
  // reminders. Off, the step is not built — the checklist is five steps and
  // nothing else changes. Task 996 puts the right-column callout on the same
  // flag: with the step hidden, a callout pointing at it would be the reminder
  // kv switched off. The server still refuses every AI action on a planless
  // account, and the app still explains that refusal.
  const showPlanNotices = useFlag('innovate.fe.plan.no-plan-notices');
  // Each example opens /services/new, so they follow that route's flag (task 1240).
  const newServiceEnabled = useFlag('innovate.fe.services.new.page');

  // The platform profile (task 596) — shares the Dashboard's cached fetch, so
  // the welcome and the greeting always name the User the same way.
  const { data: profile } = useProfile();

  // Run history — one row is enough, the total is what the step reads.
  const { data: collabsData, isPending: runsPending } = useCollaborations(1, 1);
  const runsTotal = collabsData?.pagination?.total ?? 0;

  // Any saved interface on the account (same source the services table reads).
  const { data: hasInterface, isPending: interfacesPending } = useQuery({
    queryKey: ['dashboard-onboarding-interfaces'],
    queryFn: async () => {
      try {
        const res = await generatedUiApi.list();
        return flattenVersions(res.data.data).length > 0;
      } catch {
        // Unreachable list degrades to "not done" rather than a wrong tick.
        return false;
      }
    },
    staleTime: 60_000,
  });

  const steps = deriveOnboardingSteps(
    {
      emailVerified: user?.emailVerified === true,
      hasPlan: Boolean(plan),
      servicesTotal,
      runsTotal,
      hasInterface: hasInterface === true,
    },
    showPlanNotices
  );
  const doneCount = steps.filter((step) => step.done).length;
  const nextIndex = nextStepIndex(steps);
  const greetingName = onboardingGreetingName(profile?.displayName, user?.email, user?.username);

  // ─── Instrumentation (task 541) ───────────────────────────────────────────
  // Every send goes through the consent-gated `trackEvent` seam; nothing here
  // touches the OpenPanel client directly.

  /** Impression — once per appearance of the checklist, not per render. */
  useEffect(() => {
    trackEvent('onboarding_checklist_shown', { location: currentLocationTag() });
  }, []);

  /**
   * `onboarding_step_completed` reports a *transition*: a step that was not
   * done becomes done while the checklist is on screen. A remount showing
   * already-completed steps must stay silent, so the first observation only
   * records the baseline.
   *
   * The baseline is taken once the two async signals (run history, saved
   * interfaces) have settled — before that a completed run or interface still
   * reads as "not done", and treating that as the baseline would emit a false
   * completion the moment the query resolved.
   */
  const signalsSettled = !runsPending && !interfacesPending;
  const doneSignature = steps
    .filter((step) => step.done)
    .map((step) => step.key)
    .join(',');
  const completedBaseline = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!signalsSettled) return;
    const doneKeys = doneSignature ? doneSignature.split(',') : [];
    if (completedBaseline.current === null) {
      completedBaseline.current = new Set(doneKeys);
      return;
    }
    doneKeys.forEach((key) => {
      if (completedBaseline.current?.has(key)) return;
      completedBaseline.current?.add(key);
      trackEvent('onboarding_step_completed', { step: key });
    });
  }, [signalsSettled, doneSignature]);

  const handleDismiss = () => {
    // `steps_done` is what makes the dismissal rate readable: where Users give
    // up, not just how many do.
    trackEvent('onboarding_checklist_dismissed', {
      location: currentLocationTag(),
      steps_done: doneCount,
    });
    onDismiss();
  };

  const startFromExample = (title: string, prompt: string) => {
    trackEvent('onboarding_example_card_clicked', { card: toEventSlug(title) });
    // Same prefill mechanism the AI Services first-run panel uses (task 392):
    // the composer conversation opens seeded with the described idea.
    navigate('/services/new', { state: { prompt } });
  };

  return (
    <div data-testid="dashboard-onboarding">
      {/* Header */}
      <div className="sm:flex sm:items-start sm:justify-between gap-4">
        <div>
          <h1
            className="text-2xl font-bold text-gray-900 dark:text-white"
            data-testid="dashboard-onboarding-greeting"
          >
            {greetingName ? `Welcome to Innovate, ${greetingName} 👋` : 'Welcome to Innovate 👋'}
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Let's get your first AI Service live. Everything you build happens in the{' '}
            <span className="font-semibold">AI Services</span> section — this page tracks your
            progress and, later, your stats.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="dashboard-onboarding-dismiss"
          aria-label="Dismiss the getting started checklist"
          className="mt-3 sm:mt-0 inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          <XMarkIcon className="h-4 w-4" />
          Dismiss
        </button>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-5 items-start">
        {/* Checklist */}
        <div className="lg:col-span-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 dark:border-gray-700">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Get set up</span>
            <span
              className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
              data-testid="dashboard-onboarding-progress"
            >
              <span className="w-24 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <span
                  className="block h-full bg-indigo-600"
                  style={{ width: `${(doneCount / steps.length) * 100}%` }}
                />
              </span>
              {doneCount} of {steps.length}
            </span>
          </div>

          {steps.map((step, index) => {
            const state = step.done ? 'done' : index === nextIndex ? 'next' : 'todo';
            return (
              <div
                key={step.key}
                data-testid={`dashboard-onboarding-step-${step.key}`}
                data-state={state}
                className={`flex gap-3.5 px-5 py-3.5 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                  state === 'next' ? 'bg-indigo-50/60 dark:bg-indigo-900/10' : ''
                }`}
              >
                <StepTick state={state} number={index + 1} />
                <div className="min-w-0">
                  <div
                    className={`text-sm font-semibold ${
                      step.done
                        ? 'text-gray-500 dark:text-gray-400 line-through decoration-gray-300 dark:decoration-gray-600'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {step.title}
                  </div>
                  {step.description && !step.done && (
                    <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {step.description}
                    </div>
                  )}
                </div>

                {/* Step actions */}
                {step.key === 'plan' && !step.done && (
                  <div className="ml-auto self-center flex-shrink-0">
                    <Link
                      to={CHOOSE_PLAN_PATH}
                      data-testid="dashboard-onboarding-plan-cta"
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 hover:bg-indigo-700 dark:hover:bg-indigo-800"
                    >
                      Pick a plan
                    </Link>
                  </div>
                )}
                {step.key === 'service' && !step.done && (
                  <div className="ml-auto self-center flex-shrink-0">
                    <Link
                      to="/services"
                      data-testid="dashboard-onboarding-services-cta"
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Open AI Services
                    </Link>
                  </div>
                )}
                {step.key === 'interface' && !step.done && (
                  <div className="ml-auto self-center flex-shrink-0">
                    <a
                      href={HOSTED_UI_EXAMPLE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="dashboard-onboarding-interface-example"
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      See a live example
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right column: what an AI Service is + the honest plan callout */}
        <div className="lg:col-span-2 space-y-4">
          {/* The copy is shared with the Help menu's explainer dialog (task
              605) — the card and the dialog render the same component, so they
              can never disagree. The deep-link anchor this card used to carry
              is gone with it: the menu entry opens the dialog now. */}
          <div
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm"
            data-testid="dashboard-onboarding-explainer"
          >
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 text-sm font-semibold text-gray-900 dark:text-white">
              {WHATS_AN_AI_SERVICE_TITLE}
            </div>
            <div className="px-5 py-4">
              <WhatIsAnAiServiceExplainer />
            </div>
          </div>

          {showPlanNotices && (
            <div
              className="flex gap-3 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-3.5"
              data-testid="dashboard-onboarding-plan-callout"
            >
              <div className="text-lg leading-none">💡</div>
              <div>
                <p className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                  All accounts start on Free.
                </p>
                <p className="mt-0.5 text-xs text-indigo-800 dark:text-indigo-200">
                  A paid plan adds external access: API keys, public calls, embeds and custom
                  domains. Choose one any time.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* How Innovate works (shared strip, task 526) */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
          How Innovate works
        </h2>
        <HowItWorksStrip testId="dashboard-onboarding-how-it-works" />
      </div>

      {/* Start from an example */}
      {newServiceEnabled && (
      <div className="mt-8">
        <h2 className="text-sm font-bold text-gray-900 dark:text-white">Start from an example</h2>
        <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
          Each of these opens the AI Services section with the idea already described — you just
          refine it in chat.
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          {EXAMPLES.map((example) => (
            <button
              key={example.title}
              type="button"
              onClick={() => startFromExample(example.title, example.prompt)}
              data-testid={`dashboard-onboarding-example-${example.title
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, '')}`}
              className="text-left rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm hover:border-indigo-200 dark:hover:border-indigo-700 hover:shadow-md transition-shadow"
            >
              <div className="text-xl leading-none">{example.icon}</div>
              <div className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                {example.title}
              </div>
              <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">{example.prompt}</div>
              <div className="mt-2 text-xs font-medium text-indigo-600 dark:text-indigo-400">
                Build this in chat →
              </div>
            </button>
          ))}
        </div>
      </div>
      )}

      {/* Ghost stats — the page this becomes */}
      <div className="mt-8">
        <h2 className="text-sm font-bold text-gray-900 dark:text-white">Your dashboard, soon</h2>
        <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
          Once your first service is live, this page becomes your stats home.
        </p>
        <div className="mt-3 grid gap-4 sm:grid-cols-3" data-testid="dashboard-onboarding-ghost-tiles">
          {GHOST_TILES.map((label) => (
            <div
              key={label}
              className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-4"
            >
              <div className="text-xs font-medium text-gray-400 dark:text-gray-500">{label}</div>
              <div className="mt-1.5 text-2xl font-bold text-gray-300 dark:text-gray-600">—</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
